import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useTenant } from "@/lib/tenant-context";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CreditCard, ArrowRight, LogIn, Mail, CheckCircle2 } from "lucide-react";
import cardExample1 from "@/assets/card-example-1.jpg";
import cardExample2 from "@/assets/card-example-2.jpg";
import cardExample3 from "@/assets/card-example-3.jpg";

const CARD_EXAMPLES = [
  { src: cardExample1, label: "Cartoon Style" },
  { src: cardExample2, label: "Pop Art Style" },
  { src: cardExample3, label: "Superhero Style" },
];

const TenantLanding = () => {
  const { tenant_slug } = useParams<{ tenant_slug: string }>();
  const navigate = useNavigate();
  const { tenant, loading } = useTenant();
  const { user, signInWithMagicLink } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginSent, setLoginSent] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const handleMagicLink = async () => {
    setLoginError(null);
    setLoginLoading(true);
    const { error } = await signInWithMagicLink(loginEmail);
    setLoginLoading(false);
    if (error) {
      setLoginError(error);
    } else {
      setLoginSent(true);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
        <CreditCard className="h-16 w-16 text-muted-foreground" />
        <h1 className="text-2xl font-bold font-display">Tenant Not Found</h1>
        <p className="text-muted-foreground">The link you followed doesn't match any bank.</p>
      </div>
    );
  }

  // Route waitlist tenants to the waitlist-specific landing
  if (tenant.tenantType === "waitlist") {
    return <Navigate to={`/t/${tenant_slug}/waitlist`} replace />;
  }

  const lp = tenant.landingPage;
  const displayName = tenant.brandName;
  // Replace merge fields in landing page strings
  const merge = (str: string) => str.replace(/\{\{bankName\}\}/g, displayName).replace(/Acme Bank/g, displayName);

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ "--tenant-primary": tenant.primaryColor } as React.CSSProperties}
    >
      {/* Hero */}
      <div className="relative flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        {/* Background gradient */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            background: `radial-gradient(ellipse at center, hsl(${tenant.primaryColor}), transparent 70%)`,
          }}
        />

        <div className="relative z-10 flex flex-col items-center gap-6 animate-fade-in">
          {/* Logo placeholder */}
          <div
            className="flex h-20 w-20 items-center justify-center rounded-2xl shadow-elevated"
            style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
          >
            <CreditCard className="h-10 w-10 text-white" />
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-widest" style={{ color: `hsl(${tenant.primaryColor})` }}>
              {displayName}
            </p>
            <h1 className="text-4xl font-bold font-display tracking-tight">
              {merge(lp.heroTitle)}
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              {merge(lp.heroSubtitle)}
            </p>
          </div>

          {/* Card examples showcase */}
          <div className="flex gap-4 items-end justify-center max-w-2xl w-full px-2">
            {CARD_EXAMPLES.map((card, i) => (
              <div
                key={card.label}
                className="group flex-1"
                style={{
                  transform: i === 1 ? "translateY(-8px)" : "",
                }}
              >
                <div className="overflow-hidden rounded-xl shadow-elevated transition-transform group-hover:scale-105 group-hover:-rotate-1">
                  <img
                    src={card.src}
                    alt="Example card"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-3">
            <Button
              size="lg"
              className="gradient-primary text-primary-foreground font-semibold gap-2 shadow-elevated hover:shadow-card transition-all px-8"
              onClick={() => navigate(`/t/${tenant_slug}/consent`)}
            >
              {merge(lp.ctaText)}
              <ArrowRight className="h-4 w-4" />
            </Button>

            {!user && (
              <button
                onClick={() => { setLoginOpen(true); setLoginSent(false); setLoginEmail(""); setLoginError(null); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <LogIn className="h-3.5 w-3.5" />
                Returning user? Sign in
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Magic link login dialog */}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Sign in with email
            </DialogTitle>
            <DialogDescription>
              Enter your email and we'll send you a magic link to sign in — no password needed.
            </DialogDescription>
          </DialogHeader>

          {loginSent ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="font-medium">Check your inbox!</p>
              <p className="text-sm text-muted-foreground">
                We sent a sign-in link to <strong>{loginEmail}</strong>. Click the link in the email to continue.
              </p>
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); handleMagicLink(); }}
              className="space-y-4"
            >
              <Input
                type="email"
                placeholder="you@example.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
                autoFocus
              />
              {loginError && (
                <p className="text-sm text-destructive">{loginError}</p>
              )}
              <Button type="submit" className="w-full" disabled={loginLoading || !loginEmail}>
                {loginLoading ? "Sending…" : "Send magic link"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t py-4 text-center text-xs text-muted-foreground space-y-2">
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => navigate(`/t/${tenant_slug}/privacy`)} className="underline hover:text-foreground transition-colors">Privacy Policy</button>
          <span>·</span>
          <button onClick={() => navigate(`/t/${tenant_slug}/terms`)} className="underline hover:text-foreground transition-colors">Terms & Conditions</button>
        </div>
        <p>{merge(lp.footerText)} · {displayName}</p>
      </footer>
    </div>
  );
};

export default TenantLanding;
